import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { updateCollection } from '@platform/client';
import {
  COLLECTION_NOUN,
  difficultySchema,
  illustrationObjectSchema,
  subjectSchema,
} from '@platform/schema/content';
import { useQueryClient } from '@tanstack/react-query';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { ChoiceField } from '../authoring/ChoiceField';
import { EditableText } from '../authoring/EditableText';
import { describeObject, groupOfObject } from '../authoring/illustration';

import { collectionChanged } from './collection';
import { field } from './refusals';
import { subjectLabel } from './subject';

import type { Collection } from './collection';
import type { UpdateCollectionDto } from '@platform/client';

/**
 * Everything about a collection that is not what it holds — PATH-01.
 *
 * The five columns `NewCollection` asks for, asked again of a row that already exists —
 * the detail screens could edit one of them, so the other four were permanent short of
 * SQL. Nothing here is disabled by `PUBLISHED`: the endpoint never refused these, and
 * what is frozen on a live path is its membership, not its description.
 *
 * Why that is the right line, and why the save is per field:
 * `docs/decisions/collection-settings-while-published.md`.
 */
export function CollectionSettings({ row }: { row: Collection }) {
  const queries = useQueryClient();

  const edit = useTrackedMutation(`Save the ${COLLECTION_NOUN[row.kind]}`, {
    mutationFn: async (body: UpdateCollectionDto) => {
      await updateCollection({
        path: { id: row.id },
        body,
        throwOnError: true,
      });
    },
    onSuccess: () => collectionChanged(queries, row.id),
  });

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Settings</Typography>

      <EditableText
        {...field('title')}
        value={row.title}
        disabled={edit.isPending}
        onSave={(title) => {
          edit.mutate({ title });
        }}
      />

      <EditableText
        {...field('summary')}
        value={row.summary}
        multiline
        disabled={edit.isPending}
        onSave={(summary) => {
          edit.mutate({ summary });
        }}
      />

      <ChoiceField
        {...field('subject')}
        value={row.subject}
        options={subjectSchema.options}
        describe={subjectLabel}
        disabled={edit.isPending}
        onChange={(subject) => {
          edit.mutate({ subject });
        }}
      />

      <ChoiceField
        {...field('difficulty')}
        value={row.difficulty}
        options={difficultySchema.options}
        disabled={edit.isPending}
        onChange={(difficulty) => {
          edit.mutate({ difficulty });
        }}
      />

      {/* Through the shared helpers, so both screens name and group the objects
          identically — `authoring/illustration.ts` holds the reasoning. */}
      <ChoiceField
        {...field('objectName')}
        value={row.objectName}
        options={illustrationObjectSchema.options}
        describe={describeObject}
        group={groupOfObject}
        disabled={edit.isPending}
        onChange={(objectName) => {
          edit.mutate({ objectName });
        }}
      />

      <Typography variant="caption" color="text.secondary">
        Filed as {row.slug}. Text saves when the field loses focus; a choice
        saves as you make it.
      </Typography>
    </Stack>
  );
}
